import React from 'react';
import { 
  ChartBarIcon, 
  CpuChipIcon, 
  RocketLaunchIcon,
  CommandLineIcon,
  ChartPieIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../../context/ThemeContext';
import './styles.scss';

const capabilities = [
  {
    name: 'Algorithmic Trading',
    description: 'Build and deploy automated trading strategies with our comprehensive API. Execute trades based on technical indicators, market patterns, and custom algorithms.',
    icon: CpuChipIcon,
    features: [
      'Real-time market data integration',
      'Custom strategy implementation',
      'Automated trade execution',
      'Risk management tools'
    ]
  },
  {
    name: 'Strategy Development',
    description: 'Develop and test trading strategies using historical data. Backtest your ideas and optimize performance before live deployment.',
    icon: ChartBarIcon,
    features: [
      'Historical data analysis',
      'Strategy backtesting',
      'Performance metrics',
      'Strategy optimization'
    ]
  },
  {
    name: 'Market Intelligence',
    description: 'Stay ahead of the market with advanced analytics, technical indicators, and market insights to make informed trading decisions.',
    icon: ChartPieIcon,
    features: [
      'Technical analysis tools',
      'Market trend analysis',
      'Custom indicators',
      'Real-time alerts'
    ]
  }
];

export const TradingCapabilities: React.FC = () => {
  const { isDarkMode } = useTheme();

  return (
    <div className={`relative ${isDarkMode ? 'bg-gray-900' : 'bg-white'} py-24 sm:py-32`}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl lg:text-center">
          <h2 className="text-base font-semibold leading-7 text-accent-primary">Trading Capabilities</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl text-text-primary">
            Advanced trading features for every need
          </p>
          <p className="mt-6 text-lg leading-8 text-text-secondary">
            From basic market data to advanced trading algorithms, our platform provides everything you need to succeed in the markets.
          </p>
        </div>
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
            <div className="flex flex-col">
              <dt className="text-base font-semibold leading-7 text-text-primary">
                Market Analysis
              </dt>
              <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-text-secondary">
                <p className="flex-auto">
                  Access comprehensive market analysis tools, including technical indicators, chart patterns, and market sentiment analysis.
                </p>
              </dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-base font-semibold leading-7 text-text-primary">
                Algorithmic Trading
              </dt>
              <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-text-secondary">
                <p className="flex-auto">
                  Build and deploy custom trading algorithms with our powerful API. Support for multiple trading strategies and execution methods.
                </p>
              </dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-base font-semibold leading-7 text-text-primary">
                Risk Management
              </dt>
              <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-text-secondary">
                <p className="flex-auto">
                  Implement sophisticated risk management strategies with our comprehensive suite of risk analysis tools and position management features.
                </p>
              </dd>
            </div>
          </dl>
        </div>
        <div className="mt-16 flex justify-center">
          <a
            href="#"
            className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Start Building Strategies <RocketLaunchIcon className="ml-2 h-4 w-4 inline-block" />
          </a>
        </div>
      </div>
    </div>
  );
}; 